/**
 * Supabase Invites Store
 *
 * Secure invite storage with:
 * - Cryptographic token generation
 * - SHA-256 hashing for tokens and emails
 * - Rate limiting integration
 * - Audit logging
 */

import { getSupabase } from '@/lib/supabase';
import type {
  ClientInvite,
  InviteCreateInput,
  InviteCreateResult,
  InviteRateLimitResult,
  InviteDedupeResult,
  TokenRedemptionResult,
} from '@/types/invites';
import { INVITE_TOKEN_BYTES, INVITE_TTL_HOURS, InviteCreateSchema, type InviteUpdate } from './invites.schemas';
import { reportSwallowed } from '@/lib/sentry';

// ============================================================================
// Constants
// ============================================================================

const TABLE = 'invites';
const TIMEOUT_MS = 5000;

// ============================================================================
// Crypto Utilities
// ============================================================================

/**
 * Generate cryptographically secure random token
 */
function generateSecureToken(): string {
  const bytes = new Uint8Array(INVITE_TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  // Base64url encoding (URL-safe)
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * SHA-256 hash a string (returns hex)
 */
async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash email for storage/logging (PII-safe)
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = email.toLowerCase().trim();
  return sha256(normalized);
}

/**
 * Hash token for storage
 */
export async function hashToken(token: string): Promise<string> {
  return sha256(token);
}

/**
 * Normalize email (lowercase + trim)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

// ============================================================================
// Timeout Wrapper
// ============================================================================

async function withTimeout<T>(promise: PromiseLike<T>, ms: number = TIMEOUT_MS): Promise<T> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms),
  );
  return Promise.race([Promise.resolve(promise), timeout]);
}

// ============================================================================
// Row Mapping
// ============================================================================

const mapRow = (r: Record<string, unknown>): ClientInvite => ({
  id: String(r.id),
  email: String(r.email || ''),
  emailNormalized: r.email_normalized ? String(r.email_normalized) : undefined,
  emailHash: r.email_hash ? String(r.email_hash) : undefined,
  tokenHash: r.token_hash ? String(r.token_hash) : undefined,
  clientName: r.client_name ? String(r.client_name) : undefined,
  coachId: String(r.coach_id),
  orgId: r.org_id ? String(r.org_id) : undefined,
  status: String(r.status) as ClientInvite['status'],
  createdAt: String(r.created_at),
  expiresAt: r.expires_at ? String(r.expires_at) : undefined,
  acceptedAt: r.accepted_at ? String(r.accepted_at) : undefined,
  revokedAt: r.revoked_at ? String(r.revoked_at) : undefined,
  clientId: r.client_id ? String(r.client_id) : undefined,
  sentCount: r.sent_count ? Number(r.sent_count) : undefined,
  lastSentAt: r.last_sent_at ? String(r.last_sent_at) : undefined,
  ipHash: r.ip_hash ? String(r.ip_hash) : undefined,
  userAgentHash: r.user_agent_hash ? String(r.user_agent_hash) : undefined,
  idempotencyKey: r.idempotency_key ? String(r.idempotency_key) : undefined,
});

// ============================================================================
// Store Implementation
// ============================================================================

export const supaInvitesStore = {
  /**
   * List invites by coach
   */
  async listByCoach(coachId: string): Promise<ClientInvite[]> {
    const sb = getSupabase();
    if (!sb) return [];

    const result = (await withTimeout(
      sb.from(TABLE).select('*').eq('coach_id', coachId).order('created_at', { ascending: false }),
    )) as { data: Record<string, unknown>[] | null };

    return (result.data || []).map(mapRow);
  },

  /**
   * List invites by email hash (PII-safe lookup)
   */
  async listByEmailHash(emailHash: string): Promise<ClientInvite[]> {
    const sb = getSupabase();
    if (!sb) return [];

    const result = (await withTimeout(
      sb.from(TABLE).select('*').eq('email_hash', emailHash).order('created_at', { ascending: false }),
    )) as { data: Record<string, unknown>[] | null };

    return (result.data || []).map(mapRow);
  },

  /**
   * List invites by email (for backward compatibility)
   */
  async listByEmail(email: string): Promise<ClientInvite[]> {
    const sb = getSupabase();
    if (!sb) return [];

    const normalized = normalizeEmail(email);
    const result = (await withTimeout(
      sb.from(TABLE).select('*').eq('email_normalized', normalized).order('created_at', { ascending: false }),
    )) as { data: Record<string, unknown>[] | null };

    return (result.data || []).map(mapRow);
  },

  /**
   * Get invite by ID
   */
  async get(id: string): Promise<ClientInvite | null> {
    const sb = getSupabase();
    if (!sb) return null;

    const result = (await withTimeout(sb.from(TABLE).select('*').eq('id', id).single())) as {
      data: Record<string, unknown> | null;
    };

    return result.data ? mapRow(result.data) : null;
  },

  /**
   * Get invite by token hash (for redemption)
   */
  async getByTokenHash(tokenHash: string): Promise<ClientInvite | null> {
    const sb = getSupabase();
    if (!sb) return null;

    const result = (await withTimeout(sb.from(TABLE).select('*').eq('token_hash', tokenHash).single())) as {
      data: Record<string, unknown> | null;
    };

    return result.data ? mapRow(result.data) : null;
  },

  /**
   * Create new invite with secure token
   */
  async create(input: InviteCreateInput): Promise<InviteCreateResult> {
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase not configured');

    // Validate input
    const validated = InviteCreateSchema.parse({
      email: input.email,
      clientName: input.clientName,
      coachId: input.coachId,
      ttlHours: input.ttlHours,
      idempotencyKey: input.idempotencyKey,
    });

    // Generate secure token
    const token = generateSecureToken();
    const tokenHash = await hashToken(token);

    // Normalize and hash email
    const emailNormalized = normalizeEmail(validated.email);
    const emailHash = await hashEmail(validated.email);

    // Hash IP and user agent if provided
    const ipHash = input.ipAddress ? await sha256(input.ipAddress) : null;
    const userAgentHash = input.userAgent ? await sha256(input.userAgent) : null;

    // Calculate expiration
    const ttlHours = validated.ttlHours || INVITE_TTL_HOURS;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + ttlHours * 60 * 60 * 1000);

    // Generate invite ID
    const inviteId = `inv_${Date.now()}_${crypto.randomUUID().slice(0, 8)}`;

    const result = (await withTimeout(
      sb
        .from(TABLE)
        .insert({
          id: inviteId,
          email: validated.email,
          email_normalized: emailNormalized,
          email_hash: emailHash,
          token_hash: tokenHash,
          client_name: validated.clientName ?? null,
          coach_id: validated.coachId,
          org_id: input.orgId ?? null,
          status: 'pending',
          created_at: now.toISOString(),
          expires_at: expiresAt.toISOString(),
          sent_count: 1,
          last_sent_at: now.toISOString(),
          ip_hash: ipHash,
          user_agent_hash: userAgentHash,
          idempotency_key: validated.idempotencyKey ?? null,
        })
        .select()
        .single(),
    )) as { data: Record<string, unknown>; error: { message: string } | null };

    if (result.error) throw result.error;

    const invite = mapRow(result.data);

    // Build signup URL with token (not the hash!)
    const signupUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/signup?token=${encodeURIComponent(token)}&email=${encodeURIComponent(emailNormalized)}`;

    return {
      invite,
      token, // Return raw token ONCE - caller should send in email, never store
      signupUrl,
    };
  },

  /**
   * Legacy insert (backward compatibility) - DEPRECATED
   * Use create() instead for secure token handling
   */
  async insert(inv: ClientInvite): Promise<ClientInvite> {
    const sb = getSupabase();
    if (!sb) return inv;

    const emailNormalized = normalizeEmail(inv.email);
    const emailHash = await hashEmail(inv.email);
    const expiresAt = new Date(Date.now() + INVITE_TTL_HOURS * 60 * 60 * 1000);

    const result = (await withTimeout(
      sb
        .from(TABLE)
        .insert({
          id: inv.id,
          email: inv.email.toLowerCase(),
          email_normalized: emailNormalized,
          email_hash: emailHash,
          client_name: inv.clientName ?? null,
          coach_id: inv.coachId,
          org_id: inv.orgId ?? null,
          status: inv.status,
          created_at: inv.createdAt,
          expires_at: expiresAt.toISOString(),
          accepted_at: inv.acceptedAt ?? null,
          client_id: inv.clientId ?? null,
          sent_count: 1,
          last_sent_at: inv.createdAt,
        })
        .select()
        .single(),
    )) as { data: Record<string, unknown>; error: { message: string } | null };

    if (result.error) throw result.error;
    return mapRow(result.data);
  },

  /**
   * Update invite
   */
  async update(id: string, patch: Partial<InviteUpdate>): Promise<ClientInvite | null> {
    const sb = getSupabase();
    if (!sb) return null;

    // Build update object
    const updateData: Record<string, unknown> = {};
    if (patch.status !== undefined) updateData.status = patch.status;
    if (patch.acceptedAt !== undefined) updateData.accepted_at = patch.acceptedAt;
    if (patch.revokedAt !== undefined) updateData.revoked_at = patch.revokedAt;
    if (patch.clientId !== undefined) updateData.client_id = patch.clientId;
    if (patch.sentCount !== undefined) updateData.sent_count = patch.sentCount;
    if (patch.lastSentAt !== undefined) updateData.last_sent_at = patch.lastSentAt;

    const result = (await withTimeout(sb.from(TABLE).update(updateData).eq('id', id).select().single())) as {
      data: Record<string, unknown> | null;
      error: { message: string } | null;
    };

    if (result.error) throw result.error;
    return result.data ? mapRow(result.data) : null;
  },

  /**
   * Increment resend count and update last_sent_at
   */
  async recordResend(id: string): Promise<ClientInvite | null> {
    const sb = getSupabase();
    if (!sb) return null;

    // Get current invite
    const current = await this.get(id);
    if (!current) return null;

    const newCount = (current.sentCount || 1) + 1;

    return this.update(id, {
      sentCount: newCount,
      lastSentAt: new Date().toISOString(),
    });
  },

  /**
   * Revoke an invite
   */
  async revoke(id: string): Promise<ClientInvite | null> {
    return this.update(id, {
      status: 'revoked',
      revokedAt: new Date().toISOString(),
    });
  },

  /**
   * Hard-delete an invite (e.g. sent to the wrong email). Coaches may delete
   * their own invites — enforced by the invites_delete_policy RLS policy.
   */
  async remove(id: string): Promise<boolean> {
    const sb = getSupabase();
    if (!sb) return false;

    const result = (await withTimeout(sb.from(TABLE).delete().eq('id', id))) as {
      error: { message: string } | null;
    };

    if (result.error) throw result.error;
    return true;
  },

  /**
   * Check rate limits via RPC
   */
  async checkRateLimits(coachId: string, emailHash: string): Promise<InviteRateLimitResult> {
    const sb = getSupabase();
    if (!sb) return { coachCount24h: 0, emailCount24h: 0, canSend: true };

    const rpcResult = (await withTimeout(
      sb.rpc('check_invite_rate_limit', {
        p_coach_id: coachId,
        p_email_hash: emailHash,
      }),
    )) as {
      data: Array<{ coach_count_24h: number; email_count_24h: number; can_send: boolean }> | null;
      error: unknown;
    };

    if (rpcResult.error || !rpcResult.data || !rpcResult.data[0]) {
      // Fail open if rate limit check fails (log it)
      console.warn('[invites] Rate limit check failed:', rpcResult.error);
      return { coachCount24h: 0, emailCount24h: 0, canSend: true };
    }

    const result = rpcResult.data[0];
    return {
      coachCount24h: result.coach_count_24h,
      emailCount24h: result.email_count_24h,
      canSend: result.can_send,
    };
  },

  /**
   * Check for duplicate invite (same coach + email within 24h)
   */
  async checkDedupe(coachId: string, emailHash: string): Promise<InviteDedupeResult> {
    const sb = getSupabase();
    if (!sb) return { existingInviteId: null, lastSentAt: null, isDuplicate: false };

    const rpcResult = (await withTimeout(
      sb.rpc('check_invite_dedupe', {
        p_coach_id: coachId,
        p_email_hash: emailHash,
      }),
    )) as {
      data: Array<{ existing_invite_id: string | null; last_sent_at: string | null; is_duplicate: boolean }> | null;
      error: unknown;
    };

    if (rpcResult.error || !rpcResult.data || !rpcResult.data[0]) {
      return { existingInviteId: null, lastSentAt: null, isDuplicate: false };
    }

    const result = rpcResult.data[0];
    return {
      existingInviteId: result.existing_invite_id,
      lastSentAt: result.last_sent_at,
      isDuplicate: result.is_duplicate,
    };
  },

  /**
   * Check if email is suppressed (bounced/complained)
   */
  async isEmailSuppressed(emailHash: string): Promise<boolean> {
    const sb = getSupabase();
    if (!sb) return false;

    const rpcResult = (await withTimeout(sb.rpc('is_email_suppressed', { p_email_hash: emailHash }))) as {
      data: boolean | null;
      error: unknown;
    };

    if (rpcResult.error) {
      console.warn('[invites] Suppression check failed:', rpcResult.error);
      return false;
    }

    return rpcResult.data === true;
  },

  /**
   * Redeem invite token (atomic operation)
   */
  async redeemToken(token: string, email: string, userId: string, requestId: string): Promise<TokenRedemptionResult> {
    const sb = getSupabase();
    if (!sb) return { success: false, errorCode: 'INVITE_NOT_FOUND' };

    const tokenHash = await hashToken(token);
    const emailNormalized = normalizeEmail(email);

    const rpcResult = (await withTimeout(
      sb.rpc('redeem_invite_token', {
        p_token_hash: tokenHash,
        p_email_normalized: emailNormalized,
        p_user_id: userId,
        p_request_id: requestId,
      }),
    )) as {
      data: Array<{ success: boolean; error_code?: string; invite_id?: string; coach_id?: string }> | null;
      error: unknown;
    };

    if (rpcResult.error || !rpcResult.data || !rpcResult.data[0]) {
      console.warn('[invites] Token redemption RPC failed:', rpcResult.error);
      return { success: false, errorCode: 'INVITE_NOT_FOUND' };
    }

    const result = rpcResult.data[0];
    return {
      success: result.success,
      errorCode: (result.error_code || undefined) as TokenRedemptionResult['errorCode'],
      inviteId: result.invite_id || undefined,
      coachId: result.coach_id || undefined,
    };
  },

  /**
   * Write audit log entry
   */
  async logAuditEvent(
    eventType: string,
    coachId: string,
    requestId: string,
    options: {
      inviteId?: string;
      emailHash?: string;
      ipHash?: string;
      metadata?: Record<string, unknown>;
    } = {},
  ): Promise<void> {
    const sb = getSupabase();
    if (!sb) return;

    try {
      await withTimeout(
        sb.from('invites_audit_log').insert({
          event_type: eventType,
          invite_id: options.inviteId ?? null,
          coach_id: coachId,
          email_hash: options.emailHash ?? null,
          request_id: requestId,
          ip_hash: options.ipHash ?? null,
          metadata: options.metadata ?? {},
        }),
        3000, // Shorter timeout for audit - don't block on it
      );
    } catch (e) {
      // Don't fail the main operation if audit fails
      console.warn('[invites] Audit log failed:', e);
      reportSwallowed('invites.auditLog', e);
    }
  },
};

// Export sha256 for use in other modules (others already exported at definition)
export { sha256 };
